/**
 * FormField - Generic wrapper for form fields with label and optional description
 * Implements WCAG 2.2 Level AA accessibility requirements:
 * - WCAG 3.3.1: Error Identification
 * - WCAG 3.3.2: Labels or Instructions
 * - WCAG 4.1.3: Status Messages
 */

import React, { useId } from 'react';

export interface FormFieldProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  description?: string;
  error?: string;
  children: React.ReactNode;
}

export function FormField({
  label,
  htmlFor,
  required,
  description,
  error,
  children,
}: FormFieldProps) {
  const descriptionId = useId();
  const errorId = useId();

  // Build aria-describedby string for the input field
  const describedBy =
    [description ? descriptionId : null, error ? errorId : null].filter(Boolean).join(' ') ||
    undefined;

  // Clone children to add ARIA attributes
  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<Record<string, unknown>>, {
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
      });
    }
    return child;
  });

  return (
    <div className="space-y-1">
      <label
        htmlFor={htmlFor}
        className="block text-sm font-medium text-zinc-700 dark:text-zinc-300"
      >
        {label}
        {required && (
          <span className="text-red-500 ml-1" aria-label="required">
            *
          </span>
        )}
      </label>
      {enhancedChildren}
      {description && (
        <p id={descriptionId} className="text-xs text-zinc-500 dark:text-zinc-400">
          {description}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          className="text-xs text-red-600 dark:text-red-400"
          role="alert"
          aria-live="polite"
        >
          {error}
        </p>
      )}
    </div>
  );
}
